<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'
import moment from 'moment'
import api from '@/helpers/api'
import { authComputed } from '@/state/helpers'
import StatsPeriodMetrics from './shared/stats-period-metrics.vue'
import StatsChartsCard from './shared/stats-charts-card.vue'
import {
  foamQuantities,
  millQuantities,
  isDashboardDev
} from '@/router/views/dashboards/shared/dev-data'

export default {
  name: 'stats.index',
  page: {
    title: 'Product Stats',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    StatsPeriodMetrics,
    StatsChartsCard
  },
  computed: {
    ...authComputed,
    maxYear () {
      return moment().year()
    },
    isCurrentYear () {
      return this.year === this.maxYear
    },
    startOfYear () {
      return moment()
        .year(this.year)
        .startOf('year')
        .toDate()
    },
    endOfYear () {
      if (this.isCurrentYear) return moment().toDate()
      return moment()
        .year(this.year)
        .endOf('year')
        .toDate()
    },
    startOfMonth () {
      return moment().startOf('month').toDate()
    },
    endOfMonth () {
      return moment().endOf('month').toDate()
    },
    tagsFoamBlockSize () {
      const arr = []
      this.blockSizes.forEach(blockSize => {
        this.foamTypes.forEach(foamT => {
          arr.push({
            label:
              this.$t(`attribute.block_size.${blockSize}`) +
              ' - ' +
              this.$t(`attribute.foam_type_short.${foamT}`),
            tags: [`${blockSize}`, `foam_${foamT}`]
          })
        })
      })
      return arr
    },
    tagsFoamTypes () {
      return this.foamTypes.map(foamT => ({
        label: this.$t(`attribute.foam_type_short.${foamT}`),
        tags: [`foam_${foamT}`]
      }))
    },
    tagsMillTypes () {
      return this.millTypes.map(millT => ({
        label: this.$t(`attribute.mill_type.${millT}`),
        tags: [`${millT}`]
      }))
    },
    tagsFoamMill () {
      const arr = []
      this.foamTypes.forEach(foamT => {
        this.millTypes.forEach(millT => {
          arr.push({
            label:
              this.$t(`attribute.foam_type_short.${foamT}`) +
              ' · ' +
              this.$t(`attribute.mill_type.${millT}`),
            tags: [`foam_${foamT}`, millT]
          })
        })
      })
      return arr
    },
    foamMockSeries () {
      return isDashboardDev() ? foamQuantities : null
    },
    millMockSeries () {
      return isDashboardDev() ? millQuantities : null
    },
    foamMillMockSeries () {
      if (!isDashboardDev()) return null
      const series = []
      this.foamTypes.forEach((_, fi) => {
        this.millTypes.forEach((_, mi) => {
          const foam = foamQuantities[fi] || 0
          const mill = millQuantities[mi] || 0
          // Sparse cross-product so the chart stays readable in demo mode
          const qty = Math.round((foam * mill) / 18)
          series.push(qty > 1 ? qty : fi === mi ? Math.max(1, mill % 4) : 0)
        })
      })
      return series
    },
    blockMockSeries () {
      if (!isDashboardDev()) return null
      const base = [3, 2, 1]
      const series = []
      this.blockSizes.forEach((_, bi) => {
        this.foamTypes.forEach((_, fi) => {
          series.push(
            Math.max(1, (base[bi] || 1) + (foamQuantities[fi] || 0) % 3)
          )
        })
      })
      return series
    },
    canGoPrevYear () {
      return this.year > this.minYear
    },
    canGoNextYear () {
      return this.year < this.maxYear
    }
  },
  data () {
    return {
      items: [
        {
          text: 'Statistics',
          active: true
        }
      ],
      year: moment().year(),
      minYear: 2016,
      foamTypes: ['f32', 'rg50', 'rg70', 'ld24', 's4044', 'mix'],
      blockSizes: ['block_size_s', 'block_size_l', 'block_size_xl'],
      millTypes: [
        'mill_seat',
        'mill_mattress',
        'mill_std',
        'mill_seat_back',
        'mill_other'
      ],
      totalMillings: 0
    }
  },
  async created () {
    try {
      const min = await api.webForms.fetchMinStatsYear()
      if (min) this.minYear = Number(min)
    } catch (e) {
      this.minYear = 2016
    }
  },
  methods: {
    prevYear () {
      if (this.canGoPrevYear) this.year -= 1
    },
    nextYear () {
      if (this.canGoNextYear) this.year += 1
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('stats.page_title')" :items="items" />

    <div class="portal-stats">
      <div class="portal-card portal-card--panel portal-card--header mb-2">
        <div>
          <div class="portal-card__eyebrow">{{ $t('stats.eyebrow') }}</div>
          <div class="portal-card__panel-title">
            {{ $t('dashboard.activity.overview') }}
          </div>
        </div>
        <div class="portal-year-nav">
          <button
            type="button"
            class="portal-year-nav__btn"
            :disabled="!canGoPrevYear"
            :title="$t('stats.year.previous')"
            @click="prevYear"
          >
            <i class="bx bx-caret-left"></i>
          </button>
          <span class="portal-year-nav__value">{{ year }}</span>
          <button
            type="button"
            class="portal-year-nav__btn"
            :disabled="!canGoNextYear"
            :title="$t('stats.year.next')"
            @click="nextYear"
          >
            <i class="bx bx-caret-right"></i>
          </button>
        </div>
      </div>

      <stats-period-metrics :year="year" />

      <stats-charts-card
        class="mb-2"
        :year="year"
        :foam-tags="tagsFoamTypes"
        :mill-tags="tagsMillTypes"
        :foam-mill-tags="tagsFoamMill"
        :block-tags="tagsFoamBlockSize"
        :foam-mock="foamMockSeries"
        :mill-mock="millMockSeries"
        :foam-mill-mock="foamMillMockSeries"
        :block-mock="blockMockSeries"
      />

      <div v-if="userRole === 'admin'" class="portal-admin mb-2">
        <div class="portal-card__eyebrow mb-2">
          {{ $t('stats.admin.eyebrow') }}
        </div>
        <div class="portal-grid portal-grid--admin">
          <div class="portal-card portal-card--panel">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="portal-card__panel-title">{{ year }}</div>
                <div class="text-muted small">{{ $t('Total Millings') }}</div>
              </div>
              <div class="portal-admin__total">{{ totalMillings }}</div>
            </div>
            <div
              v-for="mt in millTypes"
              :key="`${mt}-year-${year}`"
              class="portal-admin__row"
            >
              <span>{{ $t(`attribute.mill_type.${mt}`) }}</span>
              <app-quantity-stat
                :key="`mill-y-${mt}-${year}`"
                :start-date="startOfYear"
                :end-date="endOfYear"
                :tags="[`${mt}`]"
              />
            </div>
          </div>

          <div class="portal-card portal-card--panel">
            <div class="portal-card__panel-title mb-1">{{ year }}</div>
            <div class="text-muted small mb-2">
              {{ $t('stats.admin.foam_year') }}
            </div>
            <div
              v-for="foam in foamTypes"
              :key="`${foam}-year-${year}`"
              class="portal-admin__row"
            >
              <span>{{ $t(`attribute.foam_type_short.${foam}`) }}</span>
              <app-quantity-stat
                :key="`foam-y-${foam}-${year}`"
                :start-date="startOfYear"
                :end-date="endOfYear"
                :tags="[`foam_${foam}`]"
              />
            </div>
          </div>

          <div v-if="isCurrentYear" class="portal-card portal-card--panel">
            <div class="portal-card__panel-title mb-1">
              {{ $t('stats.admin.this_month') }}
            </div>
            <div class="text-muted small mb-2">{{ $t('Total Millings') }}</div>
            <div
              v-for="mt in millTypes"
              :key="`${mt}-month-${year}`"
              class="portal-admin__row"
            >
              <span>{{ $t(`attribute.mill_type.${mt}`) }}</span>
              <app-quantity-stat
                :key="`mill-m-${mt}-${year}`"
                :start-date="startOfMonth"
                :end-date="endOfMonth"
                :tags="[`${mt}`]"
              />
            </div>
          </div>

          <div v-if="isCurrentYear" class="portal-card portal-card--panel">
            <div class="portal-card__panel-title mb-1">
              {{ $t('stats.admin.this_month') }}
            </div>
            <div class="text-muted small mb-2">
              {{ $t('stats.admin.foam_month') }}
            </div>
            <div
              v-for="foam in foamTypes"
              :key="`${foam}-month-${year}`"
              class="portal-admin__row"
            >
              <span>{{ $t(`attribute.foam_type_short.${foam}`) }}</span>
              <app-quantity-stat
                :key="`foam-m-${foam}-${year}`"
                :start-date="startOfMonth"
                :end-date="endOfMonth"
                :tags="[`foam_${foam}`]"
              />
            </div>
          </div>

          <div class="portal-card portal-card--panel portal-span-2">
            <div class="portal-card__panel-title mb-1">{{ year }}</div>
            <div class="text-muted small mb-2">
              {{ $t('stats.admin.foam_block') }}
            </div>
            <template v-for="blockSize in blockSizes">
              <div
                v-for="mt in foamTypes"
                :key="`${blockSize}-${mt}-${year}`"
                class="portal-admin__row"
              >
                <span>
                  {{ $t(`attribute.block_size.${blockSize}`) }} -
                  {{ $t(`attribute.foam_type_short.${mt}`) }}
                </span>
                <app-quantity-stat
                  :key="`block-${blockSize}-${mt}-${year}`"
                  :start-date="startOfYear"
                  :end-date="endOfYear"
                  :tags="[`${mt}`, `${blockSize}`, 'mill_seat']"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.portal-stats {
  margin-bottom: 1rem;
}

.portal-card--panel {
  background: #fff;
  border-radius: 0.85rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  min-width: 0;
}

.portal-card--header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 500;
  margin-bottom: 0.15rem;
}

.portal-card__panel-title {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 600;
}

.portal-year-nav {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 0.15rem 0.35rem;
  flex: 0 0 auto;
}

.portal-year-nav__value {
  min-width: 3.25rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
}

.portal-year-nav__btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: #64748b;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  i {
    font-size: 1.25rem;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    background: #fff;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

.portal-grid--admin {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  align-items: start;

  @media (min-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.portal-span-2 {
  @media (min-width: 992px) {
    grid-column: span 2;
  }
}

.portal-admin__total {
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 600;
}

.portal-admin__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid #eef2f7;
  font-size: 0.78rem;
  color: #64748b;

  &:last-child {
    border-bottom: 0;
  }
}
</style>
